/*
 * Copyright © 2024-2026 symecode.com. All rights reserved.
 * 
 * This source code is proprietary and confidential.
 * Unauthorized copying, modification, or distribution is prohibited.
 */


import React, { useState, useEffect } from 'react';
import BackButton from '../common/BackButton';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../services/api';
import './ExecutiveReport.css';

interface MonthlyData {
  year: number;
  month: number;
  monthName: string;
  totalBids: number;
  submittedBids: number;
  acceptedBids: number;
  notAcceptedBids: number;
}

type ViewMode = 'yearly' | 'rolling';

const ExecutiveReport: React.FC = () => {
  const navigate = useNavigate();
  const [year, setYear] = useState<number>(new Date().getFullYear());
  const [monthlyData, setMonthlyData] = useState<MonthlyData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [companyName, setCompanyName] = useState<string>('');
  const [viewMode, setViewMode] = useState<ViewMode>('yearly');

  useEffect(() => {
    if (viewMode === 'yearly') {
      fetchYearlyActivity();
    } else {
      fetchRollingMonthsActivity();
    }
  }, [year, viewMode]);

  const fetchYearlyActivity = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.getExecutiveYearlyActivity(year);

      if (response.success && response.monthlyData) {
        setMonthlyData(response.monthlyData);
        setCompanyName(response.companyName || '');
      } else {
        setError(response.error || 'Failed to load activity data');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'An error occurred while fetching activity data');
      console.error('Error fetching yearly activity:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchRollingMonthsActivity = async () => {
    try {
      setLoading(true);
      setError(null);

      // Generate last 13 months of data
      const now = new Date();
      const rollingData: MonthlyData[] = [];

      for (let i = 12; i >= 0; i--) {
        const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const monthYear = date.getFullYear();
        const monthNum = date.getMonth() + 1;

        const response = await apiClient.getExecutiveMonthlyBidSummary(monthYear, monthNum, 'all');

        if (response.success) {
          const monthName = date.toLocaleDateString('en-US', { month: 'long' });
          const bids = response.bids || [];

          rollingData.push({
            year: monthYear,
            month: monthNum,
            monthName: monthName,
            totalBids: bids.length,
            submittedBids: bids.filter(b => b.submitted).length,
            acceptedBids: bids.filter(b => b.accepted).length,
            notAcceptedBids: bids.filter(b => b.submitted && !b.accepted).length
          });

          if (response.companyName && !companyName) {
            setCompanyName(response.companyName);
          }
        }
      }

      setMonthlyData(rollingData);
    } catch (err: any) {
      setError(err.response?.data?.error || 'An error occurred while fetching rolling months data');
      console.error('Error fetching rolling months activity:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleBarClick = (monthData: MonthlyData) => {
    navigate(`/executive/summary/${monthData.year}/${monthData.month}/all`);
  };

  const getMaxValue = (): number => {
    const values = monthlyData.map(m => m.totalBids);
    return Math.max(...values, 1);
  };

  // Calculate the not submitted count for stacked bar
  const getNotSubmittedCount = (monthData: MonthlyData): number => {
    return monthData.totalBids - monthData.submittedBids;
  };

  const getSubmittedNotAcceptedCount = (monthData: MonthlyData): number => {
    return monthData.submittedBids - monthData.acceptedBids;
  };

  return (
    <div className="executive-report">
      <BackButton to="/" />
      <div className="report-header">
        <h1>Executive Activity Report</h1>
        {companyName && <h2>{companyName}</h2>}
      </div>

      <div className="report-controls">
        <div className="view-mode-selector">
          <button
            onClick={() => setViewMode('yearly')}
            className={`mode-btn ${viewMode === 'yearly' ? 'active' : ''}`}
          >
            Yearly View
          </button>
          <button
            onClick={() => setViewMode('rolling')}
            className={`mode-btn ${viewMode === 'rolling' ? 'active' : ''}`}
          >
            Last 13 Months
          </button>
        </div>

        {viewMode === 'yearly' && (
          <div className="year-selector">
            <button onClick={() => setYear(year - 1)} className="year-btn">
              &lt; {year - 1}
            </button>
            <span className="current-year">{year}</span>
            <button onClick={() => setYear(year + 1)} className="year-btn">
              {year + 1} &gt;
            </button>
          </div>
        )}
      </div>

      {loading ? (
        <div className="loading">Loading activity data...</div>
      ) : error ? (
        <div className="error">{error}</div>
      ) : (
        <div className="chart-container">
          <h3 className="chart-title">Bid Activity by Month</h3>

          <div className="color-legend">
            <div className="legend-item">
              <span className="legend-color" style={{ backgroundColor: '#28a745' }}></span>
              <span className="legend-label">Accepted</span>
            </div>
            <div className="legend-item">
              <span className="legend-color" style={{ backgroundColor: '#ffc107' }}></span>
              <span className="legend-label">Submitted (Not Accepted)</span>
            </div>
            <div className="legend-item">
              <span className="legend-color" style={{ backgroundColor: '#667eea' }}></span>
              <span className="legend-label">Not Submitted</span>
            </div>
          </div>

          <div className="bar-chart">
            <div className="chart-grid">
              {monthlyData.map((monthData) => {
                const maxValue = getMaxValue();
                const notSubmittedCount = getNotSubmittedCount(monthData);
                const submittedNotAcceptedCount = getSubmittedNotAcceptedCount(monthData);
                const acceptedCount = monthData.acceptedBids;
                const totalBids = monthData.totalBids;

                // Calculate percentages for stacking
                const notSubmittedPercent = maxValue > 0 ? (notSubmittedCount / maxValue) * 100 : 0;
                const submittedNotAcceptedPercent = maxValue > 0 ? (submittedNotAcceptedCount / maxValue) * 100 : 0;
                const acceptedPercent = maxValue > 0 ? (acceptedCount / maxValue) * 100 : 0;

                return (
                  <div key={`${monthData.year}-${monthData.month}`} className="chart-column">
                    {totalBids > 0 && (
                      <div className="bar-total">{totalBids}</div>
                    )}
                    <div
                      className="bar-wrapper"
                      onClick={() => handleBarClick(monthData)}
                      title={`${monthData.monthName} ${monthData.year}: ${totalBids} total (${acceptedCount} accepted, ${submittedNotAcceptedCount} submitted, ${notSubmittedCount} not submitted)`}
                    >
                      {totalBids > 0 && (
                        <div className="stacked-bar">
                          {acceptedCount > 0 && (
                            <div
                              className="bar-section accepted"
                              style={{
                                height: `${acceptedPercent}%`,
                                backgroundColor: '#28a745'
                              }}
                            >
                              {acceptedCount > 0 && <span className="section-value">{acceptedCount}</span>}
                            </div>
                          )}
                          {submittedNotAcceptedCount > 0 && (
                            <div
                              className="bar-section submitted"
                              style={{
                                height: `${submittedNotAcceptedPercent}%`,
                                backgroundColor: '#ffc107'
                              }}
                            >
                              {submittedNotAcceptedCount > 0 && <span className="section-value">{submittedNotAcceptedCount}</span>}
                            </div>
                          )}
                          {notSubmittedCount > 0 && (
                            <div
                              className="bar-section not-submitted"
                              style={{
                                height: `${notSubmittedPercent}%`,
                                backgroundColor: '#667eea'
                              }}
                            >
                              {notSubmittedCount > 0 && <span className="section-value">{notSubmittedCount}</span>}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="month-label">
                      {monthData.monthName.substring(0, 3)}
                      {viewMode === 'rolling' && monthData.year !== year && (
                        <div className="month-year">{monthData.year}</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="chart-legend">
            <p>Click on any bar to view detailed bid summary for that month</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExecutiveReport;
